import { Tag as TagIcon } from "lucide-react";
import { useState } from "react";
import { client, useClient } from "../lib/client.ts";
import { Button, Select, SelectType, type SelectItem } from "../ui/ring.ts";

/** Tag icon that opens Ring's multi-select over the issue's tags; typing a new name offers to create it. */
export function TagPicker({ issueId, disabled }: { issueId: number; disabled?: boolean }) {
  const schema = useClient((s) => s.schema);
  const issue = useClient((s) => s.display[issueId]);
  const [busy, setBusy] = useState(false);
  if (!schema || !issue) return null;
  const data: SelectItem[] = schema.data.tags.map((t) => ({ key: t.id, label: t.name }));
  const selected = data.filter((d) => issue.tags.includes(d.key as number));
  const create = async (name: string): Promise<void> => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const id = await client.createTag(name.trim());
      client.updateIssue(issueId, { tags: [...issue.tags, id] });
    } catch {
      /* toast shown by the client */
    } finally {
      setBusy(false);
    }
  };
  return (
    <Select
      multiple
      filter={{ placeholder: "Filter or new tag" }}
      type={SelectType.CUSTOM}
      data={data}
      selected={selected}
      disabled={disabled ?? false}
      add={{ label: (s) => `Create tag “${s}”`, minlength: 1 }}
      onAdd={(name) => void create(name)}
      onChange={(items: SelectItem[]) => client.updateIssue(issueId, { tags: items.map((i) => Number(i.key)) })}
      customAnchor={({ wrapperProps, buttonProps, popup }) => (
        <span {...wrapperProps} className="relative inline-flex">
          {/* Same Ring inline icon button as its neighbours in the title row (16px glyph via index.css). */}
          <Button icon={TagIcon} id={buttonProps.id} disabled={buttonProps.disabled} onClick={buttonProps.onClick} title="Add tag" data-test={buttonProps["data-test"]} />
          {popup}
        </span>
      )}
    />
  );
}
